// ThemeModeControl — the 3-state theme picker (light / dark / system) inside the
// account Popover, rendered as one compact segmented row of icon-only options.
//
// Native-owned control (COMPONENTS §Hand-rolled vs headless): three native
// <input type="radio"> share a name, so the browser gives single-selection +
// Arrow-key roving + one tab stop for free — no hand-rolled roving. The native
// input is visually hidden; the styled segment is the visible affordance. Icon-
// only (no visible label) keeps it compact + language-proof; each option carries
// an aria-label. A <fieldset>/<legend> groups them (the account Popover's
// "Appearance" group label is the legend).
//
// `system` follows the OS via prefers-color-scheme (resolved in ThemeProvider) —
// the picker only stores the chosen mode.

import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { icons } from '../ui/icons.js';

type ThemeMode = 'light' | 'dark' | 'system';

const MODES = [
  { mode: 'light', Icon: icons.themeLight, nameKey: 'theme.modeLight' },
  { mode: 'dark', Icon: icons.themeDark, nameKey: 'theme.modeDark' },
  { mode: 'system', Icon: icons.deviceDesktop, nameKey: 'theme.modeSystem' },
] as const;

export function ThemeModeControl({
  value,
  onSelect,
  groupLabel,
}: {
  value: ThemeMode;
  onSelect: (mode: ThemeMode) => void;
  groupLabel: string;
}) {
  const { t } = useTranslation('common');
  const name = useId();
  return (
    <fieldset className="m-0 border-0 p-0 px-3 pb-1">
      <legend className="sr-only">{groupLabel}</legend>
      {/* Segmented control: a recessed track with a raised "thumb" on the active
          segment (bg-page + shadow + hairline border so it stays defined in both
          modes). Each segment is a <label> wrapping a visually-hidden native
          radio; the focus ring shows on the label when its radio is focus-visible. */}
      <div className="flex gap-1 p-1 rounded-[10px] bg-[var(--color-bg-surface-alt)]">
        {MODES.map(({ mode, Icon, nameKey }) => {
          const active = value === mode;
          return (
            <label
              key={mode}
              className={
                'flex-1 flex items-center justify-center h-8 rounded-md border transition cursor-pointer ' +
                'has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-inset has-[:focus-visible]:ring-[var(--color-primary-display)] ' +
                (active
                  ? 'bg-[var(--color-bg-page)] shadow-raised border-[var(--color-border)] text-[var(--color-primary-display)]'
                  : 'border-transparent text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-page)]/60 hover:text-[var(--color-text-primary)]')
              }
            >
              <input
                type="radio"
                name={name}
                className="sr-only"
                checked={active}
                aria-label={t(nameKey)}
                onChange={() => onSelect(mode)}
              />
              <Icon aria-hidden size={18} weight={active ? 'fill' : 'regular'} />
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}
